import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import { ObservableLevelsEnum } from '../../../../data/enums';
import { Button } from '../../../../components/atoms/Button';
import { useEffect, useState } from 'react';
import { EquipmentPartModel } from '../../../../data/types';
import DatabaseAssetsMethods from '../../../../data/database.assets.methods';
import { inspectionDB } from '../../../../data/database.structure';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import { VisualCondition } from '../../../../components/atoms/RadioButtonGroupField';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { Label } from '../../../../components/atoms/Label';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import CheckboxWithLabel from '../../../../components/atoms/Checkbox';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { flattenObjectStrings } from '../../../../utils/flattenObjectString';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';

type MachineTypeCheckboxField = {
  label: string;
  path: string;
};

const { Undercarriage } = ObservableItemIdentifier.Identifiers;

const ersCheckboxes: MachineTypeCheckboxField[] = [
  {
    label: 'Flat Spot Wear',
    path: Undercarriage.Roller.WearIndicators.FlatSpotWear,
  },
  {
    label: 'Collapsed',
    path: Undercarriage.Roller.WearIndicators.Collapsed,
  },
  {
    label: 'Excess Axle Bore Clearance',
    path: Undercarriage.Roller.WearIndicators.ExcessiveAxleBoreClearance,
  },
  {
    label: 'Thrust Washer Damaged/Missing',
    path: Undercarriage.Roller.WearIndicators.ThrustWasherDamagedMissing,
  },
  {
    label: 'Loose / Missing Retaining Hardware',
    path: Undercarriage.Roller.WearIndicators.LooseMissingRetainingHardware,
  },
  {
    label: 'Roller Path Cracking / Delamination / Plastic Deformation',
    path: Undercarriage.Roller.WearIndicators
      .RollerPathCrackingDelaminationPlasticDeformation,
  },
  {
    label: 'Severe Cracking',
    path: Undercarriage.Roller.WearIndicators.SevereCracking,
  },
];

const hexCheckboxes: MachineTypeCheckboxField[] = [
  {
    label: 'Leaking',
    path: Undercarriage.Roller.WearIndicators.Leaking,
  },
  {
    label: 'Flat Spot Wear',
    path: Undercarriage.Roller.WearIndicators.FlatSpotWear,
  },
  {
    label: 'Collapsed',
    path: Undercarriage.Roller.WearIndicators.Collapsed,
  },
  {
    label: 'Sideframe Damage',
    path: Undercarriage.Roller.WearIndicators.SideframeDamage,
  },
  {
    label: 'Loose / Missing Retaining Hardware',
    path: Undercarriage.Roller.WearIndicators.LooseMissingRetainingHardware,
  },
  {
    label: 'Roller Path Cracking / Delamination / Plastic Deformation',
    path: Undercarriage.Roller.WearIndicators
      .RollerPathCrackingDelaminationPlasticDeformation,
  },
  {
    label: 'Severe Cracking',
    path: Undercarriage.Roller.WearIndicators.SevereCracking,
  },
];

interface LoadRollerScreenProps {
  heading: string;
  subheading: string;
  equipmentPart: EquipmentPartModel;
}

export const LoadRollerScreen = ({
  equipmentPart,
  heading,
  subheading,
}: LoadRollerScreenProps) => {
  const { externalId } = useParams();
  const navigate = useNavigate();

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const [assetType, setAssetType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null); // reset error
    async function getAsset() {
      try {
        const assetMethods = new DatabaseAssetsMethods(inspectionDB);

        const asset = await assetMethods.getAssetByInspectionId(externalId!);

        if (!asset) {
          setError('Asset not found');
          return;
        }

        setAssetType(asset.model.assetType);
      } catch (error) {
        console.error('Error fetching equipment part', error);
        setError('Error fetching equipment part');
      }
    }

    if (externalId) {
      getAsset();
    }
  }, [externalId]);

  if (!equipmentPart || !assetType || !undercarriageCategory) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ObservationDraftScope inspectionId={externalId ?? ''}>
      <div>
        <ScreenHeading
          eyebrow={heading
            .replace(/-/g, ' ')
            .replace(/^./, (c) => c.toUpperCase())}
          title={subheading}
          tooltipKey={
            'undercarriage-roller-assemblies-load-roller-screen-header'
          }
        />

        <ActionStatusBar
          inspectionId={externalId!}
          inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
          dataLevel={ObservableLevelsEnum.equipmentPart}
          entities={[
            {
              entityId: equipmentPart.id,
              entityExternalId: equipmentPart.externalId,
            },
          ]}
          paths={[]}
          pathsLike={[
            ...flattenObjectStrings(Undercarriage.Roller.WearIndicators),
            Undercarriage.Roller.VisualCondition,
          ]}
          showPercentage={false}
        />

        <ScreenObservationLayout>
          <VisualCondition
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={Undercarriage.Roller.VisualCondition}
          />

          <Label titleType="fieldTitle">Wear Indicators</Label>

          {assetType === 'ERS'
            ? ersCheckboxes.map((field, i) => (
                <CheckboxWithLabel
                  label={field.label}
                  path={field.path}
                  key={field.path + i}
                  inspectionId={externalId!}
                  inspectionCategoryTypeId={
                    undercarriageCategory?.externalId ?? ''
                  }
                  dataLevel={ObservableLevelsEnum.equipmentPart}
                  entityId={equipmentPart.id}
                  entityExternalId={equipmentPart.externalId}
                  infoTooltipKey={`undercarriage-roller-assemblies-load-roller-${MdHelpers.normaliseTooltipKeyComponent(field.label)}`}
                />
              ))
            : hexCheckboxes.map((field, i) => (
                <CheckboxWithLabel
                  key={field.path + i}
                  label={field.label}
                  path={field.path}
                  inspectionId={externalId!}
                  inspectionCategoryTypeId={
                    undercarriageCategory?.externalId ?? ''
                  }
                  dataLevel={ObservableLevelsEnum.equipmentPart}
                  entityId={equipmentPart.id}
                  entityExternalId={equipmentPart.externalId}
                  infoTooltipKey={`undercarriage-roller-assemblies-load-roller-${MdHelpers.normaliseTooltipKeyComponent(field.label)}`}
                />
              ))}

          <PhotoSelector
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            title="Photos"
            path={Undercarriage.Roller.Photo}
          />

          <RichTextField
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={Undercarriage.Roller.Comments}
            label="Comments"
            placeholder="Enter your comments here..."
            className="pt-4"
          />
        </ScreenObservationLayout>

        <div className="p-4">
          <Button
            className="w-full"
            variant="primary"
            onClick={() => navigate(-1)}
          >
            COMPLETE
          </Button>
        </div>
      </div>
    </ObservationDraftScope>
  );
};
